import { Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { IRoomLogReportFilters } from "./Filters";
import { useLanguageContext } from "../../../../../contexts/LanguageContext";
import { GovernanceLogPropertyData, GovernanceLogPropertyEnum, GovernanceStatusData, GovernanceStatusEnum, IDefaultStatusData, RoomLogTypeData, RoomLogTypeEnum, RoomStatusData, RoomStatusEnum } from "../../../../../data/Enums";
import { useCallback, useEffect, useMemo } from "react";
import IRoomLog from '../../../../../interfaces/IRoomLog';
import RoomLogService from '../../../../../services/RoomLogService';
import FormHelper from '../../../../../helpers/FormHelper';
import DateTimeHelper from '../../../../../helpers/DateTimeHelper';
import IUser from "../../../../../interfaces/IUser";
import { IRoom } from "../../../../../interfaces/IPartnerPms";
import DefaultConstants from "../../../../../data/Constants";
import { Description, MeetingRoom, People, TimerOutlined } from "@mui/icons-material";
import ArrayHelper from "../../../../../helpers/ArrayHelper";
import NumberHelper from "../../../../../helpers/NumberHelper";
import useState from "../../../../../hooks/useState";


const attachmentPropertyData = GovernanceLogPropertyData[GovernanceLogPropertyEnum.Attachments];

interface IStateValues {
    roomLog: IRoomLog[];
}

const RoomLogContent: React.FunctionComponent<IRoomLogReportFilters> = (filters) => {
    const { translate } = useLanguageContext();

    const state = useState<IStateValues>({
        roomLog: []
    });


    useEffect(() => {
        let formData = FormHelper.Create(undefined);
        
        Object.entries(filters).forEach(([key, value]) => {
            if (key === 'enterprise') {
                key = 'enterpriseId';
                value = value.id;
            } else if (key === 'user') {
                value = value.map((v: IUser) => v.id).join(',');
            } else if (key === 'room') {
                value = value.map((v: IRoom) => v.id).join(',');
            } else if (key === 'action') {
                key = 'type';
                value = value.join(',');
            } else if (key === 'initialStatus') {
                value = value.map((v: IDefaultStatusData) => v.partnerId).join(',');
            } else if (key === 'finalStatus') {
                value = value.map((v: IDefaultStatusData) => v.partnerId).join(',');
            }

            formData = FormHelper.Add(formData, key, value);
        });

        RoomLogService.List(formData).then(resp => {
            state.update('roomLog', resp.data.roomLog as IRoomLog[]);
        }).catch(err => {
            state.update('roomLog', []);
        });

        // eslint-disable-next-line
    }, []);


    const renderFilterFieldset = useMemo(() => {
        return <TableCell className='reportpreview-paper-content-fieldset'>
            <Typography variant='caption' className='reportpreview-paper-content-fieldset-label'>
                {translate('filters')}
            </Typography>

            <Grid container>
                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('startDate')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {DateTimeHelper.Format(filters.startDate, 'DD/MM/YYYY')}
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('endDate')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {DateTimeHelper.Format(filters.endDate, 'DD/MM/YYYY')}
                    </Typography>
                </Grid>

                <Grid xs={filters.action.length === 0 ? 3 : 6} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('action')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.action.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.action.map(t => translate(RoomLogTypeData[t].name)).join(', ')
                            )
                        }
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('initialStatus')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.initialStatus.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.initialStatus.map(s => translate(s.name)).join(', ')
                            )
                        }
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('finalStatus')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.finalStatus.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.finalStatus.map(s => translate(s.name)).join(', ')
                            )
                        }
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('room')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.room.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.room.map(r => r.description).join(', ')
                            )
                        }
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('user')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.user.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.user.map(u => u.login).join(', ')
                            )
                        }
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('block')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.block.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.block.map(b => b.description).join(', ')
                            )
                        }
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('floor')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.floor.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.floor.map(f => f.description).join(', ')
                            )
                        }
                    </Typography>
                </Grid>

                <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                    <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                        {translate('roomType')}
                    </Typography>

                    <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                        {
                            filters.roomType.length === 0 ? (
                                translate('all')
                            ) : (
                                filters.roomType.map(r => r.description).join(', ')
                            )
                        }
                    </Typography>
                </Grid>
            </Grid>
        </TableCell>
    }, [filters, translate]);


    const data = useMemo(() => {
        let data = [...state.values.roomLog];

        if (filters.block.length > 0) {
            const block = filters.block.map(b => b.id);
            data = data.filter(r => r.room && block.includes(r.room.blockId));
        }

        if (filters.floor.length > 0) {
            const floor = filters.floor.map(f => f.id);
            data = data.filter(r => r.room && floor.includes(r.room.floorId));
        }

        if (filters.roomType.length > 0) {
            const roomType = filters.roomType.map(r => r.id);
            data = data.filter(r => r.room && roomType.includes(r.room.roomTypeId));
        }

        return data;
        // eslint-disable-next-line
    }, [state.values.roomLog, filters]);


    const createGovernanceStatusText = useCallback((governanceStatusId: string) => {
        const governanceStatus = Object.values(GovernanceStatusData).find(s => s.partnerId === governanceStatusId) as IDefaultStatusData;
        
        return <Typography className='text-title' sx={{ color: `${governanceStatus.color} !important` }}>
            <governanceStatus.Icon />
            {translate(governanceStatus.name)}
        </Typography>;

        // eslint-disable-next-line
    }, []);


    const createDoNotDisturbText = useCallback((enabled: boolean) => {
        const doNotDisturb = RoomStatusData[RoomStatusEnum.DoNotDisturb];

        let color = doNotDisturb.color;
        let description = 'enabled';

        if (!enabled) {
            color = '#bdbdbd';
            description = 'disabled';
        }

        return <Typography className='text-title' sx={{ color: `${color} !important` }}>
            <doNotDisturb.Icon />
            {translate(description)}
        </Typography>;

        // eslint-disable-next-line
    }, []);


    const _getGovernanceStatusData = useCallback((governanceStatusEnum: GovernanceStatusEnum) => {
        const statusData = GovernanceStatusData[governanceStatusEnum];

        return {
            ...statusData,
            execution: data.filter(row => row.previousValue === statusData.partnerId)
                .reduce((acc, curr) => {
                    acc.time += +(curr.executionTime ?? 0);
                    acc.length += 1;
                    return acc;
                }, { time: 0, length: 0})
        };
    }, [data]);

    
    return <>
        <TableRow>
            {renderFilterFieldset}
        </TableRow>

        <TableRow>
            <TableCell className='border-none' padding='none'>
                {
                    data.length > 0 ? (
                        <>
                            <Table className='data-table' sx={{ marginY: DefaultConstants.gridRowSpacing }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('description')}</TableCell>
                                        <TableCell width={200}>{translate('action')}</TableCell>
                                        <TableCell>{translate('changedFrom')}</TableCell>
                                        <TableCell>{translate('to')}</TableCell>
                                        <TableCell>{translate('user')}</TableCell>
                                        <TableCell>{translate('datetime')}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                
                                <TableBody className='tbody-striped'>
                                    {
                                        data.map(row => {
                                            let typeText = undefined;
                                            let previousValueText = undefined;
                                            let newValueText = undefined;
                                            
                                            if (row.type === RoomLogTypeEnum.UpdateGovernanceStatus) {
                                                typeText = '_roomLogChangeGovernanceStatusAction';

                                                previousValueText = createGovernanceStatusText(row.previousValue);
                                                newValueText = createGovernanceStatusText(row.newValue);
                                            } else {
                                                typeText = '_roomLogChangeDoNotDisturbStatusAction';
                                                
                                                previousValueText = createDoNotDisturbText(row.previousValue.toLowerCase() === 'true');
                                                newValueText = createDoNotDisturbText(row.newValue.toLowerCase() === 'true');
                                            }

                                            return <TableRow key={row.id}>
                                                <TableCell>
                                                    <Typography className='text-title'> {translate('room')} {row.room?.description ?? '-'} </Typography>
                                                    <Typography className='text-subtitle'> {row.room?.roomTypeDescription ?? '-'} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title fontweight-normal'> {translate(typeText)} </Typography>
                                                    {
                                                        row.executionTime && <Typography className='text-subtitle'> 
                                                            {translate('executedAt')}
                                                            {' '}
                                                            {DateTimeHelper.ToElapsedTime(row.executionTime)}
                                                        </Typography>
                                                    }
                                                </TableCell>

                                                <TableCell>
                                                    {previousValueText}
                                                </TableCell>

                                                <TableCell>
                                                    {newValueText}
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> {row.user.login} </Typography>
                                                    <Typography className='text-subtitle'> {row.user.fullName} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> {DateTimeHelper.Format(row.updatedAt, 'DD/MM/YYYY')} </Typography>
                                                    <Typography className='text-subtitle'> {DateTimeHelper.Format(row.updatedAt, 'HH:mm:ss')} </Typography>
                                                </TableCell>

                                                <TableCell className='display-inlineflex'>
                                                    {
                                                        row.attachments !== undefined && row.attachments.length > 0 && (
                                                            <Box className='verticalstatus-box width-auto'>
                                                                <attachmentPropertyData.Icon />
                                                                <Typography>{row.attachments.length} {translate(attachmentPropertyData.name).toLowerCase()}</Typography>
                                                            </Box>
                                                        )
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        })
                                    }
                                </TableBody>
                            </Table>

                            {/* Summary */}
                            <Table className='page-break show-only-in-print'>
                                {renderFilterFieldset}
                            </Table>

                            <Table className='data-table' sx={{ marginTop: DefaultConstants.gridRowSpacing }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell colSpan={2}>{translate('summary')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                
                                <TableBody className='tbody-striped'>
                                    <TableRow>
                                        <TableCell className='color-primary display-flex alignitems-center fontweight-600 gap-10'>
                                            <Description className='fontsize-20' />
                                            {translate('recordsFound')}
                                        </TableCell>
                                        <TableCell className='textalign-right fontweight-600'>{data.length}</TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell className='display-flex alignitems-center fontweight-600 gap-10'>
                                            <MeetingRoom className='fontsize-20' />
                                            {translate('differentRooms')}
                                        </TableCell>
                                        <TableCell className='textalign-right fontweight-600'>
                                            {ArrayHelper.DistinctBy(data.filter(row => row.room).map(row => row.room) as IRoom[], 'id').length}
                                        </TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell className='display-flex alignitems-center fontweight-600 gap-10'>
                                            <People className='fontsize-20' />
                                            {translate('differentUsers')}
                                        </TableCell>
                                        <TableCell className='textalign-right fontweight-600'>
                                            {ArrayHelper.DistinctBy(data.map(row => row.user), 'id').length}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>

                            {/* Statistics */}
                            <Table className='data-table' sx={{ marginTop: DefaultConstants.gridRowSpacing }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell colSpan={2}>{translate('governanceStatistics')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                
                                <TableBody className='tbody-striped'>
                                    {
                                        [
                                            _getGovernanceStatusData(GovernanceStatusEnum.InTidying),
                                            _getGovernanceStatusData(GovernanceStatusEnum.Inspection)
                                        ].map((status, key) => {
                                            const { time: executionTime, length: executionLength } = status.execution;

                                            const average = NumberHelper.Average(executionTime, executionLength)

                                            return <>
                                                {
                                                    key > 0 && <TableRow sx={{ height: DefaultConstants.gridRowSpacing * 8 }} />
                                                }

                                                <TableRow className='backgroundcolor-striped'>
                                                    <TableCell 
                                                        className='display-flex alignitems-center fontweight-600 gap-10' 
                                                        sx={{
                                                            color: `${status.color} !important`
                                                        }}
                                                    >
                                                        <status.Icon className='fontsize-20' />
                                                        {translate(status.name)}
                                                    </TableCell>

                                                    <TableCell className='textalign-right fontweight-600'>
                                                        {executionLength}
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow className='backgroundcolor-paper'>
                                                    <TableCell className='display-flex alignitems-center fontweight-600 gap-10' >
                                                        <TimerOutlined className='fontsize-20' />
                                                        {translate('totalTime')}
                                                    </TableCell>

                                                    <TableCell className='textalign-right fontweight-600'>
                                                        {DateTimeHelper.ToElapsedTime(executionTime)}
                                                    </TableCell>
                                                </TableRow>

                                                <TableRow className='backgroundcolor-paper'>
                                                    <TableCell className='display-flex alignitems-center fontweight-600 gap-10' >
                                                        <TimerOutlined className='fontsize-20' />
                                                        {translate('averageTime')}
                                                    </TableCell>

                                                    <TableCell className='textalign-right fontweight-600'>
                                                        {DateTimeHelper.ToElapsedTime(Math.ceil(average))}
                                                    </TableCell>
                                                </TableRow>
                                            </>;
                                        })
                                    }
                                </TableBody>
                            </Table>
                        </>
                    ) : (
                        <Box className='reportpreview-paper-content-notfound'>
                            <Typography variant='body1'>{translate('_roomLogEmptyRoomList')}</Typography>
                        </Box>
                    )
                }
            </TableCell>
        </TableRow>
    </>;
}

export default RoomLogContent;